import { z } from 'zod'
import { parseLineupLiveCursor } from '@/lib/realtime/lineupLiveEventCursor'
import { isVerifiedUnscoredDraft } from './unscoredDraft'
import type { DashboardPrimarySnapshot } from '@/lib/dashboard/readiness/types'

const uuid = z.string().uuid()
const nonEmpty = z.string().trim().min(1)
const providerPlayerId = nonEmpty

const scopeSchema = z.object({
  schemaVersion: z.number().int().positive(),
  userId: uuid,
  seasonId: uuid,
  instanceId: uuid,
  gameweekId: uuid,
  gameweekNumber: z.number().int().min(1).max(38),
  playMode: z.enum(['cash', 'token']),
  activeLobbyId: uuid.nullable()
})

const lineupSlotSchema = z.object({
  slotId: nonEmpty,
  gamePlayerId: providerPlayerId,
  order: z.number().int().min(0),
  formationPosition: z.number().int().min(1).max(11).nullable(),
  position: z.enum(['GK', 'DEF', 'MID', 'FWD']),
  displayName: nonEmpty,
  clubName: nonEmpty,
  currentPrice: z.number().finite().nonnegative().optional(),
  photoUrl: z.string().url().nullable().optional(),
  teamCode: z.string().nullable().optional(),
  starRating: z.number().finite().nullable().optional()
})

const snapshotSchema = z.object({
  schemaVersion: z.number().int().positive(),
  scope: scopeSchema,
  instance: z.object({
    teamName: nonEmpty,
    tokenBalance: z.number().finite().nonnegative(),
    playableState: z.literal('playable')
  }),
  lineup: z.object({
    lineupId: uuid,
    revision: nonEmpty,
    formation: nonEmpty,
    starters: z.array(lineupSlotSchema),
    bench: z.array(lineupSlotSchema),
    reserves: z.array(lineupSlotSchema),
    manager: z.object({
      gamePlayerId: providerPlayerId,
      displayName: nonEmpty,
      clubName: nonEmpty,
      currentPrice: z.number().finite().nonnegative().optional(),
      photoUrl: z.string().url().nullable().optional(),
      teamCode: z.string().nullable().optional(),
      starRating: z.number().finite().nullable().optional()
    }),
    captainPlayerId: providerPlayerId,
    viceCaptainPlayerId: providerPlayerId,
    wildcardState: z
      .object({
        type: nonEmpty,
        active: z.boolean()
      })
      .nullable(),
    lockState: z.object({
      status: z.enum(['open', 'locked']),
      deadlineAt: z.string().datetime({ offset: true }).nullable()
    })
  }),
  score: z.object({
    authority: z.enum([
      'pre_deadline',
      'provisional_live',
      'settlement_pending',
      'official_settled'
    ]),
    total: z.number().finite(),
    greenScore: z.number().finite(),
    redScore: z.number().finite(),
    playerBreakdown: z.array(
      z.object({
        gamePlayerId: providerPlayerId,
        points: z.number().finite(),
        greenPoints: z.number().finite(),
        redPoints: z.number().finite(),
        isCurrentlyOnField: z.boolean()
      })
    ),
    managerPoints: z.number().finite(),
    benchPoints: z.number().finite(),
    eventWatermark: z.string().nullable(),
    settlementRevision: z.string().trim().min(1).nullable()
  }),
  competition: z.object({
    mode: z.enum(['cash', 'token']),
    lobbyState: z.enum([
      'entry_unavailable',
      'entry_available',
      'accepted_queued',
      'waiting_composition',
      'assigned',
      'authoritative_zero',
      'member',
      'manager_only'
    ]),
    lobbyIdentity: z
      .object({
        lobbyId: uuid,
        name: nonEmpty
      })
      .nullable(),
    competitionFormat: z.enum(['h2h', 'points']).nullable(),
    primaryStatus: z.enum(['available', 'waiting', 'active', 'settling', 'settled'])
  }),
  repair: z.object({
    status: z.literal('clear'),
    blockingTasks: z.tuple([])
  }),
  snapshot: z.object({
    revision: nonEmpty,
    generatedAt: z.string().datetime(),
    freshness: z.literal('current')
  })
})

export class DashboardPrimaryValidationError extends Error {
  constructor(readonly issues: string[]) {
    super(`Dashboard primary snapshot is not coherent: ${issues.join(', ')}`)
    this.name = 'DashboardPrimaryValidationError'
  }
}

function semanticIssues(snapshot: DashboardPrimarySnapshot): string[] {
  const issues: string[] = []
  const allSlots = [
    ...snapshot.lineup.starters,
    ...snapshot.lineup.bench,
    ...snapshot.lineup.reserves
  ]
  const starterIds = new Set(snapshot.lineup.starters.map((slot) => slot.gamePlayerId))
  const starterFormationPositions = snapshot.lineup.starters.map((slot) => slot.formationPosition)

  if (snapshot.scope.schemaVersion !== snapshot.schemaVersion) {
    issues.push('scope_schema_version_mismatch')
  }

  if (snapshot.lineup.starters.length === 0) issues.push('lineup_starters_missing')
  if (
    starterFormationPositions.some((position) => position === null) ||
    new Set(starterFormationPositions).size !== starterFormationPositions.length ||
    starterFormationPositions.some(
      (position) => position !== null && position > starterFormationPositions.length
    )
  ) {
    issues.push('starter_formation_positions_invalid')
  }
  if (!starterIds.has(snapshot.lineup.captainPlayerId)) issues.push('captain_not_in_starters')
  if (!starterIds.has(snapshot.lineup.viceCaptainPlayerId)) {
    issues.push('vice_captain_not_in_starters')
  }
  if (snapshot.lineup.captainPlayerId === snapshot.lineup.viceCaptainPlayerId) {
    issues.push('captaincy_not_distinct')
  }
  if (new Set(allSlots.map((slot) => slot.slotId)).size !== allSlots.length) {
    issues.push('lineup_slot_ids_not_unique')
  }
  if (new Set(allSlots.map((slot) => slot.gamePlayerId)).size !== allSlots.length) {
    issues.push('lineup_players_not_unique')
  }

  const scorePlayerIds = new Set(snapshot.score.playerBreakdown.map((entry) => entry.gamePlayerId))
  const unscoredDraft = isVerifiedUnscoredDraft({
    authority: snapshot.score.authority,
    locked: snapshot.lineup.lockState.status !== 'open',
    deadlineAt: snapshot.lineup.lockState.deadlineAt,
    observedAt: Date.parse(snapshot.snapshot.generatedAt),
    statCount: snapshot.score.playerBreakdown.length,
    totals: [
      snapshot.score.total,
      snapshot.score.greenScore,
      snapshot.score.redScore,
      snapshot.score.managerPoints,
      snapshot.score.benchPoints
    ]
  })
  if (
    !unscoredDraft &&
    snapshot.lineup.starters.some((slot) => !scorePlayerIds.has(slot.gamePlayerId))
  ) {
    issues.push('score_breakdown_incomplete')
  }

  if (
    (snapshot.score.authority === 'provisional_live' ||
      snapshot.score.authority === 'settlement_pending') &&
    !snapshot.score.eventWatermark
  ) {
    issues.push('provisional_score_watermark_missing')
  }
  if (snapshot.score.eventWatermark && !parseLineupLiveCursor(snapshot.score.eventWatermark)) {
    issues.push('score_event_watermark_invalid')
  }
  if (snapshot.score.authority === 'official_settled' && !snapshot.score.settlementRevision) {
    issues.push('official_settlement_revision_missing')
  }
  if (snapshot.competition.mode !== snapshot.scope.playMode) {
    issues.push('competition_mode_scope_mismatch')
  }
  if (snapshot.scope.activeLobbyId !== (snapshot.competition.lobbyIdentity?.lobbyId ?? null)) {
    issues.push('competition_lobby_scope_mismatch')
  }
  if (
    (snapshot.competition.lobbyState === 'member' ||
      snapshot.competition.lobbyState === 'manager_only' ||
      snapshot.competition.lobbyState === 'assigned') &&
    !snapshot.competition.lobbyIdentity
  ) {
    issues.push('competition_lobby_identity_missing')
  }

  return issues
}

export function parseDashboardPrimarySnapshot(value: unknown): DashboardPrimarySnapshot {
  const parsed = snapshotSchema.safeParse(value)
  if (!parsed.success) {
    throw new DashboardPrimaryValidationError(
      parsed.error.issues.map((issue) => `schema:${issue.path.join('.') || 'root'}`)
    )
  }

  const snapshot: DashboardPrimarySnapshot = parsed.data
  const issues = semanticIssues(snapshot)
  if (issues.length > 0) throw new DashboardPrimaryValidationError(issues)
  return snapshot
}
